import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Formik,
  FastField,
  Form,
} from 'formik';
import * as Sentry from '@sentry/browser';

import { ReactComponent as CheckIcon } from '../../../../../../assets/icons/v2/check-circle.svg';
import { ReactComponent as CancelIcon } from '../../../../../../assets/icons/v2/cross-cancel.svg';

import UserContext from '../../../../../../context/UserContext';
import ExternalCoachContext from '../../../../../context/ExternalCoachContext';
import InputLabel from '../../../../../../components/v2/InputLabel';
import {
  HeaderRow,
  Title,
} from '../../../../../../components/v2/Header';
import FormikInput from '../../../../../../components/v2/FormikInput';
import { PrimaryButton } from '../../../../../../components/Button/ActionButtons';
import {
  SectionContainer,
  SectionHeaderContainer,
  SectionTitle,
  SectionCompartment,
  SectionFooterContainer,
} from '../../../../../../components/v2/Section';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../../hooks/useSessionStore';
import Track, { LocationType, TrackType } from '../../../../../../Model/Track';
import { useHyroxEnabled } from '../../../../../../utils/postHog';
import {
  DifficultyLevel,
} from '../../../../../../Model/Program';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import useToast from '../../../../../hooks/useToast';
import { InfoTag } from '../../../../../../components/Tags';
import RadioButtonGroup from '../../../../../components/RadioButtonGroup';
import useLogger from '../../../../../../hooks/useLogger';
import { CoachingActivity } from '../../../../../../utils/log';
import ManageProgramContext from '../../../../../context/ManageProgramContext';
import ProgramListSelector from './components/ProgramListSelector';
import {
  Container,
  FormSection,
  Label,
  StyledFormRowScale,
  EquipmentContainer,
  FieldContent,
  StyledSaveIcon,
  ProgramListSection,
  ContainerContent,
} from './styles';
import { fieldName, programAttributes } from './formFields';
import {
  initialValues as startValues,
  validationSchema,
  initialProgramValues,
} from './validation';
import texts from './texts';

const difficultyLevelOptions = Object.values(DifficultyLevel).map((difficultyLevel) => ({
  label: texts.difficultyLevel[difficultyLevel],
  value: difficultyLevel,
}));

const locationTypeOptions = Object.values(LocationType).map((locationType) => ({
  label: texts.locationType[locationType],
  value: locationType,
}));

const trackTypeOptions = Object.values(TrackType).map((trackType) => ({
  label: texts.trackType[trackType],
  value: trackType,
}));

const TrackEditor = ({
  trackDoc,
  onClose,
  isEditView,
}) => {
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const { isAdmin } = useSessionStore();
  const { userId: coachId } = useContext(UserContext);
  const { coachDoc } = useContext(ExternalCoachContext);
  const { coachPrograms } = useContext(ManageProgramContext);

  const [isLoading, setIsLoading] = useState(false);

  const { logCoachingActivity } = useLogger();

  // Hyrox is rolled out per coach — without the flag the track type selector is
  // hidden and tracks are saved as STANDARD (existing hyrox tracks keep their type).
  const showTrackTypeSelector = useHyroxEnabled(coachId);

  const getFormattedPrograms = useCallback((programs) => {
    const assignedPrograms = [];
    programs.forEach((programEntry) => {
      if (!programEntry.programRef) {
        assignedPrograms.push({ ...initialProgramValues });
        return;
      }

      const programDetails = coachPrograms.find(({ id }) => id === programEntry.programRef);
      const lastRow = assignedPrograms[assignedPrograms.length - 1];

      // Collapse consecutive duplicate programRefs into a single row with an incremented repeatCount.
      if (lastRow && lastRow.id === programEntry.programRef) {
        lastRow[programAttributes.REPEAT_COUNT] = (lastRow[programAttributes.REPEAT_COUNT] || 1) + 1;
        return;
      }

      if (!programDetails) {
        assignedPrograms.push({
          id: programEntry.programRef,
          label: `${texts.missingProgram} (${programEntry.programRef})`,
          program: null,
          [programAttributes.REPEAT_COUNT]: 1,
        });
      } else {
        assignedPrograms.push({
          id: programDetails.id,
          label: programDetails.name,
          program: programDetails,
          [programAttributes.REPEAT_COUNT]: 1,
        });
      }
    });
    return assignedPrograms;
  }, [
    coachPrograms,
  ]);

  const initialValues = useMemo(() => (
    trackDoc
      ? {
        ...trackDoc.data,
        [fieldName.PROGRAMS]: getFormattedPrograms(trackDoc.programs),
        [fieldName.TYPE]: trackDoc.type,
        [fieldName.LOCATION]: trackDoc.location,
        [fieldName.EQUIPMENT]: trackDoc.equipment,
      }
      : startValues
  ), [
    trackDoc,
    getFormattedPrograms,
  ]);

  const handleSubmit = useCallback(async (values) => {
    setIsLoading(true);
    try {
      const programsList = values[fieldName.PROGRAMS].flatMap((program) => {
        if (!program?.id) return [];
        const repeatCount = Math.max(1, Math.trunc(Number(program[programAttributes.REPEAT_COUNT]) || 1));
        return Array.from({ length: repeatCount }, () => ({
          programRef: program.id,
          programName: program.label || '',
          workoutCount: program.program?.workouts?.length || 0,
        }));
      });
      // Derive workoutsPerWeek from the first program's non-null workoutRefs.
      // Rest days have no workoutRef and should not be counted.
      const firstProgram = values[fieldName.PROGRAMS].find((p) => p?.id);
      const workoutsPerWeek = firstProgram?.program?.workouts
        ? firstProgram.program.workouts.filter((w) => !!w.workoutRef).length
        : 0;

      const isPublicTracksCoach = !!coachDoc?.isPublicTracksCoach;
      // Hyrox tracks have no difficulty/location — every Hyrox subscriber gets the
      // same track, so the criteria fields are not written for them.
      const isHyroxTrack = values[fieldName.TYPE] === TrackType.HYROX;
      const trackData = {
        name: values[fieldName.NAME],
        programs: programsList,
        workoutsPerWeek,
        type: values[fieldName.TYPE] || TrackType.STANDARD,
        ...(!isHyroxTrack && !!values[fieldName.DIFFICULTY] && { difficulty: values[fieldName.DIFFICULTY] }),
        ...(!isHyroxTrack && !!values[fieldName.LOCATION] && { location: values[fieldName.LOCATION] }),
        equipment: values[fieldName.EQUIPMENT],
        ...(!trackDoc
          ? {
            coach: coachId,
            isPublic: isPublicTracksCoach,
            isArchived: false,
            createdAt: new Date().getTime(),
          }
          : {
            updatedAt: new Date().getTime(),
          }),
      };
      if (trackDoc) {
        await trackDoc.updateFields(trackData);
        logCoachingActivity(CoachingActivity.UPDATED_TRACK, { trackId: trackDoc.id });
      } else {
        const track = await Track.addDoc(trackData);
        logCoachingActivity(CoachingActivity.CREATED_TRACK, { trackId: track.id });
      }
      if (isComponentMountedRef.current) {
        showToast(texts.trackSaved);
        setIsLoading(false);
        onClose();
      }
    } catch (error) {
      Sentry.captureException(error);
      if (isComponentMountedRef.current) {
        showToast(texts.trackSaveError, { error: true });
        setIsLoading(false);
      }
    }
  }, [
    showToast,
    isComponentMountedRef,
    coachId,
    coachDoc,
    trackDoc,
    onClose,
    logCoachingActivity,
  ]);

  return (
    <Container>
      <HeaderRow>
        <Title>{isEditView ? texts.headerTitle.edit : texts.headerTitle.create}</Title>
      </HeaderRow>
      <SectionContainer>
        <SectionHeaderContainer>
          <SectionTitle>{isEditView ? texts.headerTitle.edit : texts.headerTitle.create}</SectionTitle>
        </SectionHeaderContainer>
        <SectionCompartment>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({
              values,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form>
                <ContainerContent>
                  <SectionCompartment>
                    {isEditView && trackDoc && isAdmin && (
                      <FormSection>
                        <InputLabel>{`${texts.trackIdLabel}:`}</InputLabel>
                        <div>{trackDoc.id}</div>
                      </FormSection>
                    )}
                    <FormSection>
                      <FormikInput
                        name={fieldName.NAME}
                        label={`${texts.field[fieldName.NAME]}:`}
                      />
                    </FormSection>
                    <ProgramListSection>
                      <FieldContent>
                        <ProgramListSelector />
                      </FieldContent>
                    </ProgramListSection>
                    {showTrackTypeSelector && (
                      <FormSection>
                        <FieldContent>
                          <Label>{`${texts.field[fieldName.TYPE]}:`}</Label>
                          <StyledFormRowScale>
                            <RadioButtonGroup
                              options={trackTypeOptions}
                              showStyledOptions={false}
                              selectedOption={values[fieldName.TYPE] || TrackType.STANDARD}
                              onOptionChange={(value) => setFieldValue(fieldName.TYPE, value)}
                            />
                          </StyledFormRowScale>
                        </FieldContent>
                      </FormSection>
                    )}
                    {values[fieldName.TYPE] !== TrackType.HYROX && (
                      <>
                        <FormSection>
                          <FieldContent>
                            <Label>{`${texts.field[fieldName.DIFFICULTY]}:`}</Label>
                            <StyledFormRowScale>
                              <RadioButtonGroup
                                options={difficultyLevelOptions}
                                showStyledOptions={false}
                                selectedOption={values[fieldName.DIFFICULTY]}
                                onOptionChange={(value) => setFieldValue(fieldName.DIFFICULTY, value)}
                              />
                            </StyledFormRowScale>
                          </FieldContent>
                        </FormSection>
                        <FormSection>
                          <FieldContent>
                            <Label>{`${texts.field[fieldName.LOCATION]}:`}</Label>
                            <StyledFormRowScale>
                              <RadioButtonGroup
                                options={locationTypeOptions}
                                showStyledOptions={false}
                                selectedOption={values[fieldName.LOCATION]}
                                onOptionChange={(value) => setFieldValue(fieldName.LOCATION, value)}
                              />
                            </StyledFormRowScale>
                          </FieldContent>
                        </FormSection>
                      </>
                    )}
                    <FormSection>
                      <FastField name={fieldName.EQUIPMENT}>
                        {({ field }) => (
                          <FieldContent>
                            <Label>{`${texts.field[fieldName.EQUIPMENT]}:`}</Label>
                            {field.value.length > 0 ? (
                              <EquipmentContainer>
                                {field.value.map((equipment) => (
                                  <InfoTag key={equipment}>{equipment}</InfoTag>
                                ))}
                              </EquipmentContainer>
                            ) : (
                              <div>{texts.emptyValue}</div>
                            )}
                          </FieldContent>
                        )}
                      </FastField>
                    </FormSection>
                  </SectionCompartment>
                  <SectionFooterContainer>
                    <PrimaryButton
                      type="submit"
                      size="medium"
                      icon={isEditView ? <StyledSaveIcon /> : <CheckIcon />}
                      disabled={isSubmitting}
                    >
                      {isEditView ? texts.saveChanges : texts.addNewTrack}
                    </PrimaryButton>
                    <PrimaryButton
                      onClick={onClose}
                      icon={<CancelIcon />}
                      variant="info"
                      size="medium"
                      disabled={isSubmitting}
                    >
                      {texts.cancel}
                    </PrimaryButton>
                  </SectionFooterContainer>
                </ContainerContent>
              </Form>
            )}
          </Formik>
        </SectionCompartment>
      </SectionContainer>
      <LoadingOverlay isLoading={isLoading} />
    </Container>
  );
};

TrackEditor.propTypes = {
  trackDoc: PropTypes.shape({
    id: PropTypes.string,
    data: PropTypes.shape({}),
    programs: PropTypes.arrayOf(PropTypes.shape({})),
    type: PropTypes.string,
    location: PropTypes.string,
    equipment: PropTypes.arrayOf(PropTypes.string),
    updateFields: PropTypes.func,
  }),
  onClose: PropTypes.func.isRequired,
  isEditView: PropTypes.bool,
};

TrackEditor.defaultProps = {
  trackDoc: null,
  isEditView: false,
};

export default TrackEditor;
